import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Camera, Wifi, WifiOff, Search, Plus, Monitor } from 'lucide-react';

interface HikvisionCamera {
  device_id: string;
  current_ip: string;
  last_known_ip: string;
  port: number;
  status: 'online' | 'offline' | 'discovering';
  brand: string;
  model: string;
  stream_url?: string;
}

interface DiscoveredCamera {
  ip_address: string;
  device_id: string;
  brand: string;
  model: string;
  port: number;
  status: string;
}

const HikvisionManager: React.FC = () => {
  const [cameras, setCameras] = useState<HikvisionCamera[]>([]);
  const [discoveredCameras, setDiscoveredCameras] = useState<DiscoveredCamera[]>([]);
  const [loading, setLoading] = useState(false);
  const [monitoring, setMonitoring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form states
  const [newCamera, setNewCamera] = useState({
    device_id: '',
    username: 'admin',
    password: '',
    ip_address: '',
    port: 80
  });
  
  const [discoveryForm, setDiscoveryForm] = useState({
    username: 'admin',
    password: '',
    network_range: '192.168.1.0/24'
  });

  const API_BASE = process.env.REACT_APP_API_URL || 'https://csense.airivu.in';

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchCameras = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/hikvision/cameras`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setCameras(data.cameras || []);
      }
    } catch (err) {
      console.error('Failed to fetch cameras:', err);
    }
  };

  const addCamera = async () => {
    if (!newCamera.device_id || !newCamera.password) {
      setError('Device ID and password are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/hikvision/add-camera`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newCamera)
      });

      const data = await response.json();

      if (response.ok) {
        await fetchCameras();
        setNewCamera({
          device_id: '',
          username: 'admin',
          password: '',
          ip_address: '',
          port: 80
        });
      } else {
        setError(data.detail || 'Failed to add camera');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const discoverCameras = async () => {
    if (!discoveryForm.password) {
      setError('Password is required for discovery');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/hikvision/discover`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(discoveryForm)
      });

      const data = await response.json();

      if (response.ok) {
        setDiscoveredCameras(data.discovered_cameras || []);
      } else {
        setError(data.detail || 'Discovery failed');
      }
    } catch (err) {
      setError('Discovery network error');
    } finally {
      setLoading(false);
    }
  };

  const addDiscoveredCamera = async (camera: DiscoveredCamera) => {
    const cameraData = {
      device_id: camera.device_id,
      username: discoveryForm.username,
      password: discoveryForm.password,
      ip_address: camera.ip_address,
      port: camera.port
    };

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/hikvision/add-camera`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(cameraData)
      });

      if (response.ok) {
        await fetchCameras();
        // Remove from discovered list
        setDiscoveredCameras(prev => prev.filter(c => c.device_id !== camera.device_id));
      }
    } catch (err) {
      setError('Failed to add discovered camera');
    } finally {
      setLoading(false);
    }
  };

  const toggleMonitoring = async () => {
    setLoading(true);
    try {
      const endpoint = monitoring ? 'stop-monitoring' : 'start-monitoring';
      const response = await fetch(`${API_BASE}/api/hikvision/${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setMonitoring(!monitoring);
      }
    } catch (err) {
      setError('Failed to toggle monitoring');
    } finally {
      setLoading(false);
    }
  };

  const getSnapshot = async (deviceId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/hikvision/snapshot/${deviceId}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        // Open snapshot in new window
        const newWindow = window.open();
        if (newWindow) {
          newWindow.document.write(`
            <html>
              <head><title>Camera Snapshot - ${deviceId}</title></head>
              <body style="margin:0; display:flex; justify-content:center; align-items:center; min-height:100vh; background:#000;">
                <img src="data:image/jpeg;base64,${data.snapshot}" style="max-width:100%; max-height:100vh;" />
              </body>
            </html>
          `);
        }
      }
    } catch (err) {
      setError('Failed to get snapshot');
    }
  };

  useEffect(() => {
    fetchCameras();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Hikvision Camera Manager</h2>
        <div className="flex gap-2">
          <Button
            onClick={toggleMonitoring}
            variant={monitoring ? "destructive" : "default"}
            disabled={loading}
          >
            {monitoring ? <WifiOff className="w-4 h-4 mr-2" /> : <Wifi className="w-4 h-4 mr-2" />}
            {monitoring ? 'Stop Monitoring' : 'Start Monitoring'}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Add Camera Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Add Hikvision Camera
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="device_id">Device ID</Label>
              <Input
                id="device_id"
                value={newCamera.device_id}
                onChange={(e) => setNewCamera(prev => ({ ...prev, device_id: e.target.value }))}
                placeholder="hikvision_camera_01"
              />
            </div>
            <div>
              <Label htmlFor="ip_address">IP Address (Optional)</Label>
              <Input
                id="ip_address"
                value={newCamera.ip_address}
                onChange={(e) => setNewCamera(prev => ({ ...prev, ip_address: e.target.value }))}
                placeholder="192.168.1.100"
              />
            </div>
            <div>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={newCamera.username}
                onChange={(e) => setNewCamera(prev => ({ ...prev, username: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={newCamera.password}
                onChange={(e) => setNewCamera(prev => ({ ...prev, password: e.target.value }))}
              />
            </div>
          </div>
          <Button onClick={addCamera} disabled={loading}>
            {loading ? 'Adding...' : 'Add Camera'}
          </Button>
        </CardContent>
      </Card>

      {/* Discovery Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Discover Cameras
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="disc_username">Username</Label>
              <Input
                id="disc_username"
                value={discoveryForm.username}
                onChange={(e) => setDiscoveryForm(prev => ({ ...prev, username: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="disc_password">Password</Label>
              <Input
                id="disc_password"
                type="password"
                value={discoveryForm.password}
                onChange={(e) => setDiscoveryForm(prev => ({ ...prev, password: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="network_range">Network Range</Label>
              <Input
                id="network_range"
                value={discoveryForm.network_range}
                onChange={(e) => setDiscoveryForm(prev => ({ ...prev, network_range: e.target.value }))}
              />
            </div>
          </div>
          <Button onClick={discoverCameras} disabled={loading}>
            {loading ? 'Discovering...' : 'Discover Cameras'}
          </Button>

          {discoveredCameras.length > 0 && (
            <div className="mt-4">
              <h4 className="font-semibold mb-2">Discovered Cameras:</h4>
              <div className="space-y-2">
                {discoveredCameras.map((camera) => (
                  <div key={camera.device_id} className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <span className="font-medium">{camera.ip_address}</span>
                      <span className="text-sm text-gray-500 ml-2">({camera.brand} {camera.model})</span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => addDiscoveredCamera(camera)}
                      disabled={loading}
                    >
                      Add
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Camera List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5" />
            Hikvision Cameras ({cameras.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {cameras.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No Hikvision cameras added yet</p>
          ) : (
            <div className="space-y-4">
              {cameras.map((camera) => (
                <div key={camera.device_id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Camera className="w-8 h-8 text-blue-600" />
                      <div>
                        <h4 className="font-semibold">{camera.device_id}</h4>
                        <p className="text-sm text-gray-600">
                          Current IP: {camera.current_ip || 'Unknown'}
                        </p>
                        <p className="text-sm text-gray-600">
                          Last Known: {camera.last_known_ip}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={camera.status === 'online' ? 'default' : 'secondary'}>
                        {camera.status}
                      </Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => getSnapshot(camera.device_id)}
                      >
                        <Monitor className="w-4 h-4 mr-1" />
                        Snapshot
                      </Button>
                    </div>
                  </div>
                  {camera.stream_url && (
                    <p className="text-xs text-gray-500 mt-2 font-mono">
                      Stream: {camera.stream_url}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HikvisionManager;